import { useRef, useState } from 'react';
import Image from 'next/image';
import type SwiperClass from 'swiper';
import { A11y, Mousewheel } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Box, Flex, Heading, IconButton, TabPanel, TabPanels, Text, useBreakpointValue } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@udacity/chakra-uds-icons';

import { BriefCaseIcon } from '~/components/icons';
import { TabbedSection } from '~/components/tabbed-section';
import { trackEvent } from '~/features/analytics/track-event';
import { SchoolCareerPath } from '~/features/school/models/school-content';

import { SCHOOL_LP_ANALYTICS_EVENT } from '../constants';
import { SchoolLpContent } from '../models/school-lp-content';
import { SchoolLpProgram } from '../models/school-lp-program';

import { CareerPathPanel, CareerPathRole } from './career-path-panel';

import 'swiper/css';
import 'swiper/css/mousewheel';

type CareerPathsSectionProps = NonNullable<StaticCareerPathsSectionProps>;

export function CareerPathsSection({
  eyebrow,
  title,
  description,
  skillsTitle,
  startingPointsTitle,
  roles,
  theme,
}: CareerPathsSectionProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef<SwiperClass | null>(null);
  // Swiper's loop mode crashes when initialized inside a `display: none`
  // container, so the mobile carousel only mounts while the mobile layout is
  // active instead of being CSS-hidden on desktop.
  const isMobile = useBreakpointValue({ base: true, md: false }, { fallback: 'md' });

  const panelTheme = {
    roleNameColor: theme.eyebrowColor,
    bodyColor: theme.bodyColor,
    asideBg: theme.asideBg,
    programCardBadgeImage: theme.programCardBadgeImage,
  };

  const handleIndexChange = (index: number) => {
    setActiveIndex(index);
    trackEvent({
      name: SCHOOL_LP_ANALYTICS_EVENT,
      type: 'click',
      label: 'career_paths_clicked',
      value: roles[index]?.name,
    });
  };

  return (
    <Box as='section' overflow='hidden' position='relative'>
      <Box bottom={0} height='100%' position='absolute' right={0} width='100%' aria-hidden>
        <Image
          alt=''
          sizes='100vw'
          src={theme.backgroundImage}
          style={{ objectFit: 'cover', objectPosition: 'bottom right' }}
          fill
        />
      </Box>
      <Box position='relative'>
        <TabbedSection
          bgColor='transparent'
          headerColor={theme.eyebrowColor}
          headerIcon={<BriefCaseIcon color={theme.eyebrowColor} h='18px' w='18px' />}
          headerSubtitle={eyebrow}
          sectionGap={{ base: '40px', md: '48px' }}
          sectionPadding={{ base: '64px 0px', md: '96px 0px' }}
          tabListProps={{ bgColor: theme.tabsBg, borderRadius: '8px', display: { base: 'none', md: 'flex' } }}
          tabProps={{ color: 'grey.300' }}
          tabSelectedProps={{ bgColor: theme.tabSelectedBg, boxShadow: 'none', color: 'white' }}
          tabs={roles.map((role) => ({ id: role.id, label: role.tabLabel }))}
          tabsProps={{ index: activeIndex, onChange: handleIndexChange }}
          headerDescription={
            <Text
              color={theme.bodyColor}
              fontSize={{ base: '0.875rem', md: '1rem' }}
              lineHeight='1.5'
              margin='0 auto'
              maxWidth='842px'
            >
              {description}
            </Text>
          }
          headerTitle={
            <Heading as='h2' color='white' size={{ base: 'mobile-heading-2', md: 'heading-2' }}>
              {title}
            </Heading>
          }
        >
          <Flex
            alignItems='center'
            bg={theme.tabsBg}
            borderRadius='8px'
            boxShadow='inset 0px 6px 6px 0px rgba(0, 0, 0, 0.04)'
            display={{ base: 'flex', md: 'none' }}
            marginBottom='8px'
            padding='6px'
          >
            <Flex
              alignItems='center'
              bg={theme.tabSelectedBg}
              borderRadius='4px'
              justifyContent='space-between'
              padding='12px'
              width='100%'
            >
              <IconButton
                _active={{ bg: 'whiteAlpha.300' }}
                _hover={{ bg: 'whiteAlpha.200' }}
                aria-label='Previous role'
                color='white'
                height='24px'
                icon={<ChevronLeftIcon height='18px' width='18px' />}
                minWidth='24px'
                padding={0}
                variant='ghost'
                width='24px'
                onClick={() => swiperRef.current?.slidePrev()}
              />
              <Text color='white' fontSize='0.8125rem' fontWeight='500' lineHeight='1.4'>
                {roles[activeIndex]?.tabLabel}
              </Text>
              <IconButton
                _active={{ bg: 'whiteAlpha.300' }}
                _hover={{ bg: 'whiteAlpha.200' }}
                aria-label='Next role'
                color='white'
                height='24px'
                icon={<ChevronRightIcon height='18px' width='18px' />}
                minWidth='24px'
                padding={0}
                variant='ghost'
                width='24px'
                onClick={() => swiperRef.current?.slideNext()}
              />
            </Flex>
          </Flex>
          <TabPanels
            backdropFilter='blur(24px)'
            bg={theme.panelBg}
            border='1px solid'
            borderBottomLeftRadius='8px'
            borderBottomRightRadius='16px'
            borderColor={theme.panelBorderColor}
            borderTopLeftRadius='8px'
            borderTopRightRadius='8px'
            display={{ base: 'none', md: 'block' }}
            padding='8px'
          >
            {roles.map((role) => (
              <TabPanel key={role.id} padding={0}>
                <CareerPathPanel
                  role={role}
                  skillsTitle={skillsTitle}
                  startingPointsTitle={startingPointsTitle}
                  theme={panelTheme}
                />
              </TabPanel>
            ))}
          </TabPanels>
          {/* No `autoHeight` on the Swiper: resizing the wrapper to the active
              slide makes the page below jump on every swipe. The equal-height
              slide styles keep the container at the tallest panel instead. */}
          <Box
            display={{ base: 'block', md: 'none' }}
            marginX='-16px'
            sx={{ '.swiper .swiper-slide': { display: 'flex', height: 'auto' } }}
          >
            {isMobile && (
              <Swiper
                centeredSlides={true}
                initialSlide={activeIndex}
                loop={true}
                modules={[A11y, Mousewheel]}
                mousewheel={{ forceToAxis: true, thresholdDelta: 10 }}
                slidesPerView='auto'
                spaceBetween={16}
                style={{ touchAction: 'pan-y' }}
                onSlideChange={(swiper) => handleIndexChange(swiper.realIndex)}
                onDestroy={() => {
                  swiperRef.current = null;
                }}
                onSwiper={(swiper) => {
                  swiperRef.current = swiper;
                }}
              >
                {roles.map((role) => (
                  <SwiperSlide key={role.id} style={{ width: 'calc(100vw - 32px)' }}>
                    <Box
                      backdropFilter='blur(24px)'
                      bg={theme.panelBg}
                      border='1px solid'
                      borderColor={theme.panelBorderColor}
                      borderRadius='8px 8px 16px 16px'
                      padding='16px'
                      width='100%'
                    >
                      <CareerPathPanel
                        role={role}
                        skillsTitle={skillsTitle}
                        startingPointsTitle={startingPointsTitle}
                        theme={panelTheme}
                      />
                    </Box>
                  </SwiperSlide>
                ))}
              </Swiper>
            )}
          </Box>
        </TabbedSection>
      </Box>
    </Box>
  );
}

export function transformStaticCareerPathsSectionProps({
  content,
  programsByKey,
  careerPaths,
}: {
  content: SchoolLpContent;
  programsByKey: Record<string, SchoolLpProgram>;
  /** Sanity school entry's `careerPaths` roles, rendered in that order. */
  careerPaths: SchoolCareerPath[] | null;
}) {
  if (!content.careerPaths || !careerPaths?.length) {
    return null;
  }

  const { eyebrow, title, description, skillsTitle, startingPointsTitle } = content.careerPaths;

  const resolvedRoles: CareerPathRole[] = careerPaths.map((role) => ({
    id: role._id,
    tabLabel: role.tabLabel,
    name: role.name,
    pictogramImage: role.pictogram?.asset.url ?? null,
    description: role.description,
    skills: role.skills ?? [],
    programs: (role.programs ?? []).map((program) => programsByKey[program.key]).filter(Boolean),
  }));

  return {
    eyebrow,
    title,
    description,
    skillsTitle,
    startingPointsTitle,
    roles: resolvedRoles,
    theme: {
      backgroundImage: content.theme.careerPaths.backgroundImage,
      tabsBg: content.theme.careerPaths.tabsBg,
      tabSelectedBg: content.theme.colors.accentDark,
      panelBg: content.theme.careerPaths.panelBg,
      panelBorderColor: content.theme.careerPaths.panelBorderColor,
      asideBg: content.theme.careerPaths.asideBg,
      eyebrowColor: content.theme.colors.accentLight,
      bodyColor: content.theme.colors.bodyOnDark,
      programCardBadgeImage: content.theme.programCardBadgeImage,
    },
  };
}

export type StaticCareerPathsSectionProps = ReturnType<typeof transformStaticCareerPathsSectionProps>;
